import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { CaretLeft, CaretRight, CornersOut } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useRef } from 'react';
import Lightbox from 'react-spring-lightbox';
import styled, { css } from 'styled-components';

import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import NextItemImagePreloader from '@/pages/BiddingConsole/components/InlineLightboxCarousel/components/NextItemImagePreloader';
import useShowMobileConsole from '@/pages/BiddingConsole/hooks/useShowMobileConsole';
import useConsoleCarouselData from '@/pages/ImageCarouselPopout/hooks/useConsoleCarouselData';
import { useAppSelector } from '@/redux/hooks';
import { getLocale } from '@/redux/modules/intl';
import { useTrackItemImageGalleryInteractionAnalytics } from '@/utils/amplitude/analytics/item/trackItemImageGalleryInteraction.analytics';
import { popupCenter } from '@/utils/popupCenter';
import { getConsoleImageCarouselUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    itemId: number;
};

const InlineLightboxCarousel = ({ catalogId, itemId }: Props) => {
    const { canNext, canPrev, currentImageIndex, gotoNext, gotoPrevious, mappedPhotos, title, totalPhotosCount } =
        useConsoleCarouselData({
            catalogId,
            initialIndex: 0,
            itemId,
        });

    const locale = useAppSelector(getLocale);
    const imageNum = currentImageIndex + 1;
    const windowRef = useRef(null);
    const timerRef = useRef<ReturnType<typeof setInterval>>(null);

    const localePath = locale === 'en-gb' ? `/${locale}` : '';

    const showMobileConsole = useShowMobileConsole();
    const trackImageGalleryInteraction = useTrackItemImageGalleryInteractionAnalytics();
    const resolvedMappedPhotos = mappedPhotos ?? [];

    const trackLightboxNavigation = useCallback(
        (nextIndex: number, interactionType: 'Next' | 'Prev') => {
            if (!resolvedMappedPhotos.length) {
                return;
            }
            trackImageGalleryInteraction({
                imagePosition: nextIndex + 1,
                interactionType,
                totalImages: resolvedMappedPhotos.length,
            });
        },
        [resolvedMappedPhotos.length, trackImageGalleryInteraction]
    );

    const handleNext = useCallback(() => {
        const nextIndex = canNext ? currentImageIndex + 1 : 0;
        trackLightboxNavigation(nextIndex, 'Next');
        gotoNext();
    }, [canNext, currentImageIndex, gotoNext, trackLightboxNavigation]);

    const handlePrevious = useCallback(() => {
        const nextIndex = canPrev ? currentImageIndex - 1 : resolvedMappedPhotos.length - 1;
        trackLightboxNavigation(nextIndex, 'Prev');
        gotoPrevious();
    }, [canPrev, currentImageIndex, gotoPrevious, resolvedMappedPhotos.length, trackLightboxNavigation]);

    const openImageCarouselPopout = useCallback(() => {
        trackImageGalleryInteraction({
            imagePosition: imageNum,
            interactionType: 'Open Lightbox',
            totalImages: totalPhotosCount,
        });

        windowRef.current = popupCenter({
            height: 1000,
            title: `${title} Image`,
            url: localePath + getConsoleImageCarouselUrl(catalogId, itemId, imageNum),
            width: 1000,
        });

        timerRef.current = setInterval(() => {
            // Apparently this is the standard security work around for keeping track of whether a newly opened window has closed
            if (windowRef.current.closed) {
                clearInterval(timerRef.current);
            }
        }, 1000);
    }, [catalogId, imageNum, itemId, localePath, title, totalPhotosCount, trackImageGalleryInteraction]);

    const isSsr = useIsServerSideRendered();

    if (isSsr) {
        return null;
    }

    return (
        <>
            <LightboxContainer $showMobileConsole={showMobileConsole}>
                <Lightbox
                    currentIndex={currentImageIndex}
                    images={resolvedMappedPhotos}
                    inline
                    isOpen
                    onNext={handleNext}
                    onPrev={handlePrevious}
                    renderNextButton={() => (
                        <IconButton
                            className="absolute right-sm z-10 xsMax:hidden"
                            data-testid="inlineLightboxNextButton"
                            disabled={!canNext}
                            icon={CaretRight}
                            iconStyle="bold"
                            onClick={handleNext}
                            size="sm"
                            variant="outlined"
                        />
                    )}
                    renderPrevButton={() => (
                        <IconButton
                            className="absolute left-sm z-10 xsMax:hidden"
                            data-testid="inlineLightboxPrevButton"
                            disabled={!canPrev}
                            icon={CaretLeft}
                            iconStyle="bold"
                            onClick={handlePrevious}
                            size="sm"
                            variant="outlined"
                        />
                    )}
                    singleClickToZoom
                />
                <NextItemImagePreloader itemId={itemId} />
                {!showMobileConsole && (
                    <IconButton
                        className="absolute bottom-xs right-xs"
                        icon={CornersOut}
                        onClick={openImageCarouselPopout}
                        shape="square"
                    />
                )}
            </LightboxContainer>

            <ImageCountContainer>
                <Caption>
                    <FormattedMessage
                        id="bidding_console.console.description.images_label"
                        values={{
                            currentImageNumber: imageNum,
                            totalImages: totalPhotosCount,
                        }}
                    />
                </Caption>
            </ImageCountContainer>
        </>
    );
};

export default InlineLightboxCarousel;

const ImageCountContainer = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 4px;
    user-select: none;
`;
const LightboxContainer = styled.div<{ $showMobileConsole: boolean }>`
    overflow: hidden;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    max-height: 384px;
    flex: 0 0 auto;
    position: relative;

    ${({ $showMobileConsole }) =>
        $showMobileConsole &&
        css`
            border: none;
            max-height: 253px;
            img {
                border: 1px solid ${({ theme }) => theme.colors.grey400};
            }
        `}
`;
